import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Settings2, Truck } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SHIPPING_QUEUE_META } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Shipping() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState(null);
  const [settings, setSettings] = useState(null);
  const [settingsForm, setSettingsForm] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const isOwner = user.role === "owner";

  const load = useCallback(async () => {
    try {
      const [{ data: q }, { data: st }] = await Promise.all([api.get("/shipping/queue"), api.get("/settings")]);
      setQueue(q);
      setSettings(st);
      setSettingsForm((prev) => prev ?? st);
    } catch (e) {
      toast.error(apiError(e));
      setQueue([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveSettings = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/settings", {
        weight_rounding: settingsForm.weight_rounding,
        insurance_pct: Number(settingsForm.insurance_pct),
        uninsured_compensation_multiplier: Number(settingsForm.uninsured_compensation_multiplier),
        shipping_cutoff: settingsForm.shipping_cutoff,
      });
      setSettings(data);
      setSettingsForm(data);
      toast.success("Shipping settings saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="shipping-page">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight">Shipping</h1>
          <p className="text-sm text-muted-foreground">Packed orders ready for dispatch</p>
        </div>
        {isOwner && (
          <Button variant="outline" data-testid="shipping-settings-button" className="h-12 shrink-0 px-3" onClick={() => setSettingsOpen(!settingsOpen)}>
            <Settings2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {isOwner && settingsOpen && settingsForm && (
        <div className="mb-5 space-y-3 rounded-md border bg-accent/30 p-4" data-testid="shipping-settings-panel">
          <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Shipping Settings (Owner)</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1.5">
              <Label>Weight rounding</Label>
              <Select value={settingsForm.weight_rounding} onValueChange={(v) => setSettingsForm({ ...settingsForm, weight_rounding: v })}>
                <SelectTrigger data-testid="settings-rounding" className="h-11 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ceil_kg" data-testid="settings-rounding-ceil-kg">Round up to kg</SelectItem>
                  <SelectItem value="ceil_half_kg" data-testid="settings-rounding-ceil-half">Round up to 0.5 kg</SelectItem>
                  <SelectItem value="none" data-testid="settings-rounding-none">No rounding</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Insurance rate %</Label>
              <Input
                data-testid="settings-insurance"
                type="number"
                min="0"
                step="0.1"
                className="h-11 bg-white"
                value={settingsForm.insurance_pct}
                onChange={(e) => setSettingsForm({ ...settingsForm, insurance_pct: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Uninsured compensation ×</Label>
              <Input
                data-testid="settings-compensation"
                type="number"
                min="0"
                className="h-11 bg-white"
                value={settingsForm.uninsured_compensation_multiplier}
                onChange={(e) => setSettingsForm({ ...settingsForm, uninsured_compensation_multiplier: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Cutoff (HH:MM WIB)</Label>
              <Input
                data-testid="settings-cutoff"
                className="h-11 bg-white"
                placeholder="10:00"
                value={settingsForm.shipping_cutoff}
                onChange={(e) => setSettingsForm({ ...settingsForm, shipping_cutoff: e.target.value })}
              />
            </div>
          </div>
          <Button data-testid="settings-save" className="h-11 w-full" onClick={saveSettings} disabled={busy}>
            Save Settings
          </Button>
        </div>
      )}

      {queue === null ? (
        <div className="grid place-items-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        </div>
      ) : queue.length === 0 ? (
        <div data-testid="shipping-queue-empty" className="grid place-items-center rounded-md border border-dashed py-20 text-center">
          <Truck className="mb-2 h-8 w-8 text-stone-300" />
          <p className="font-medium text-muted-foreground">No orders ready to ship</p>
          <p className="mt-1 text-xs text-muted-foreground">Orders appear here automatically once Packing marks them PACKED</p>
        </div>
      ) : (
        <div className="space-y-2.5" data-testid="shipping-queue">
          {queue.map((e) => {
            const meta = SHIPPING_QUEUE_META[e.queue_status] || SHIPPING_QUEUE_META.ready;
            return (
              <button
                key={e.order_id}
                data-testid={`shipping-card-${e.order_id}`}
                onClick={() => navigate(`/shipping/orders/${e.order_id}`)}
                className="w-full rounded-md border bg-card p-4 text-left transition-transform hover:-translate-y-[1px] active:scale-[0.99]"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold">{e.customer_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {e.batch_name} · Order #{e.order_number}
                    </p>
                  </div>
                  <span data-testid={`shipping-status-${e.order_id}`} className={`shrink-0 rounded-md border px-2 py-0.5 text-xs font-bold ${meta.badge}`}>
                    {meta.label}
                  </span>
                </div>
                <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                  <span className="rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-[11px] font-bold text-stone-700" data-testid={`shipping-units-${e.order_id}`}>
                    {e.packed_units} packed unit{e.packed_units === 1 ? "" : "s"}
                  </span>
                  {e.packed_at && (
                    <span className="rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-[11px] font-semibold text-stone-600" data-testid={`shipping-packed-at-${e.order_id}`}>
                      Packed {format(new Date(e.packed_at), "d MMM yyyy, HH:mm")}
                    </span>
                  )}
                  {e.shipment?.courier && (
                    <span className="rounded-md border bg-[#EDE4DE] px-2 py-0.5 text-[11px] font-semibold text-stone-600">
                      {e.shipment.courier}{e.shipment.awb ? ` · ${e.shipment.awb}` : ""}
                    </span>
                  )}
                  {e.shipment?.unpaid_override && (
                    <span className="rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-[11px] font-bold text-red-700">SHIP-BEFORE-PAYMENT</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
